<svelte:options customElement={{ tag: "bt-interests-modal", shadow: "none" }} />

<script lang="ts">
  import { onMount } from 'svelte';
  import ActionButton from '$shared/action-button/ActionButton.svelte';

  export let heading = '';
  export let body = '';
  export let headingId = 'interests-modal-heading';
  export let personalizeText = 'Personalize my learning';
  export let skipText = 'Skip for now';
  export let interestsPath = '/interests/edit';
  export let promptedPath = '/interests/prompted';

  let dialog: HTMLDialogElement | null = null;

  // Records that the modal was answered. keepalive lets the request finish even
  // though the personalize button navigates away immediately
  const markPrompted = () => {
    fetch(promptedPath, {
      method: 'PATCH',
      credentials: 'same-origin',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute('content')
      }
    }).catch(() => {
      // Nothing to recover here.
    });
  };

  const handleSkip = () => {
    window.amplitude?.track('Interest Modal Skip Clicked');
    markPrompted();
    dialog?.close();
  };

  // Separate from markPrompted so each button records its own choice.
  const handlePersonalize = () => {
    window.amplitude?.track('Interest Modal Personalize Clicked');
    markPrompted();
  };

  onMount(() => {
    const el = document.querySelector<HTMLDialogElement>('dialog.interests-modal');
    if (!el) return;
    dialog = el;

    // Swallowing the escape key makes the two buttons the only ways out, so we
    // can record the prompt on every exit.
    const blockEscape = (event: Event) => event.preventDefault();
    el.addEventListener('cancel', blockEscape);

    // showModal is what gives us the backdrop and keeps focus inside the modal.
    // Focus goes to the dialog rather than the first button, otherwise the
    // browser rings that button before anyone has touched the keyboard.
    const open = () => {
      if (el.open) return;

      el.showModal();
      el.focus();
    };

    // A new user in an org with the splash turned on sees the video first. The
    // splash takes itself off the page when it finishes, and the modal opens then.
    const splash = document.querySelector('.welcome-splash');
    let observer: MutationObserver | null = null;

    if (splash) {
      observer = new MutationObserver(() => {
        if (document.body.contains(splash)) return;

        observer?.disconnect();
        open();
      });
      observer.observe(document.body, {childList: true, subtree: true});
    } else {
      open();
    }

    return () => {
      observer?.disconnect();
      el.removeEventListener('cancel', blockEscape);
    };
  });
</script>

<div class="interests-modal__graphic" aria-hidden="true" role="presentation">
  <span class="interests-modal__card interests-modal__card--left">Influencing</span>
  <span class="interests-modal__card interests-modal__card--center">
    <span class="interests-modal__marker"></span>Emotional Intelligence
  </span>
  <span class="interests-modal__card interests-modal__card--right">Adaptability</span>
</div>

<!-- The copy carries a <br/> for the line break the design asks for. -->
<h2 class="interests-modal__heading" id={headingId}>{@html heading}</h2>
<p class="interests-modal__body">{@html body}</p>

<ActionButton design="primary" link={interestsPath} on:click={handlePersonalize}>
  {personalizeText}
</ActionButton>
<ActionButton design="text-button" on:click={handleSkip}>
  {skipText}
</ActionButton>

<style>
  :global(dialog.interests-modal) {
    width: 560px;
    max-width: calc(100vw - 2rem);
    padding: 24px;
    border: none;
    border-radius: 8px;
    background: white;
    box-shadow:
      0 20px 30px -5px rgb(0 0 0 / 15%),
      0 8px 16px -2px rgb(0 0 0 / 10%),
      0 2px 4px 0 rgb(0 0 0 / 5%);
  }
  :global(dialog.interests-modal:focus) {
    outline: none;
  }
  :global(dialog.interests-modal::backdrop) {
    background: theme('colors.neutral.600');
    opacity: 0.6;
  }

  :global(dialog.interests-modal bt-interests-modal) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
  }

  .interests-modal__heading {
    font-family: theme('fontFamily.serif');
    font-size: 24px;
    font-weight: 400;
    line-height: 30px;
    letter-spacing: -0.5px;
    color: theme('colors.black');
    text-align: center;
    margin: 0;
  }
  .interests-modal__body {
    font-size: 16px;
    font-weight: 400;
    line-height: 26px;
    color: theme('colors.neutral.600');
    text-align: center;
    margin: 0;
  }

  .interests-modal__graphic {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 140px;
    width: 100%;
  }
  .interests-modal__card {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: white;
    border: 1px solid theme('colors.neutral.200');
    border-radius: 8px;
    box-shadow:
      0 6px 5px rgb(82 82 82 / 20%),
      0 3px 11px rgb(82 82 82 / 10%),
      0 4px 4px rgb(82 82 82 / 2%);
    padding: 16px;
    font-size: 16px;
    font-weight: 500;
    line-height: 26px;
    white-space: nowrap;
  }
  .interests-modal__card--left {
    transform: rotate(-8deg);
    margin-right: -10px;
  }
  .interests-modal__card--center {
    position: relative;
    z-index: 1;
    border-color: theme('colors.black');
  }
  .interests-modal__card--right {
    transform: rotate(8deg);
    margin-left: -10px;
  }
  .interests-modal__marker {
    width: 8px;
    height: 8px;
    background: theme('colors.yellow');
    flex-shrink: 0;
  }

  @media (max-width: 34rem) {
    .interests-modal__card {
      font-size: 13px;
      padding: 10px;
    }
    .interests-modal__heading :global(br),
    .interests-modal__body :global(br) {
      display: none;
    }
  }
</style>
